import { createSelector } from '@reduxjs/toolkit';

import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectResolvedPathname } from 'src/redux/navigation/selectors';
import { selectRouteParseOptions } from 'src/redux/productsQuery/selectors/selectRouteParseOptions';
import { safeCanonicalPath } from 'src/utils/safeCanonicalPath';
import { stripToPath } from 'src/utils/selectTaxonomyContentEntry';
import { selectExactTaxonomyEntry } from './selectExactTaxonomyEntry';

export const CANONICAL_ORIGIN = 'https://www.notonthehighstreet.com';

/** The `seo.canonical_url_override` from the current URL's own (exact) entry, if any. */
const selectCanonicalOverride = (state: ReduxApplicationState): string | undefined =>
  selectExactTaxonomyEntry(state)?.content?.canonicalOverride;

/**
 * The current path in canonical form via the package's canonical path (drops
 * query/`page`, retains surface + filter segments, normalises trailing slash /
 * empty `/-/`). Case and encoding are preserved — filter values can be mixed-case
 * (e.g. `/-/mat/Gold`) and the canonical must not lower-case them the way
 * matching does. Falls back to {@link stripToPath} when the route options or a
 * parse are unavailable (defensive — the taxonomy route always has a surface),
 * so even the fallback emits a consistently-shaped path.
 */
const selectCanonicalPath = createSelector(
  [selectResolvedPathname, selectRouteParseOptions],
  (pathname, options): string =>
    (options && safeCanonicalPath(pathname, options)) ?? stripToPath(pathname),
);

/**
 * The taxonomy PLP's `<link rel="canonical">` href (always emitted).
 *
 * Self-referencing by default: an absolute URL of the current path in canonical
 * form (see {@link selectCanonicalPath} — query params incl. `page` stripped,
 * surface and filter segments retained, trailing slash / empty `/-/`
 * normalised). Index suppression for non-indexable URLs is handled by the
 * `noindex` meta robots tag (see
 * {@link selectTaxonomyRobotsContent}), not by cross-pointing the default
 * canonical elsewhere.
 *
 * The one exception is `seo.canonical_url_override` from the URL's own entry, which
 * wins when present (the field is TBC in Contentstack — read defensively). An
 * override is a deliberate authoring choice and may point elsewhere, so it can
 * coexist with `noindex`; that is the author's responsibility, not the default.
 */
export const selectTaxonomyCanonical = createSelector(
  [selectCanonicalPath, selectCanonicalOverride],
  (path, override): string => override ?? `${CANONICAL_ORIGIN}${path}`,
);
